import type { Role, TenantUser } from '@/shared/api/generated/tenancy';
import type { BadgeTone } from '@/shared/ui/StatusBadge';

/* The presentation decisions behind the Admin screens, separated from the rendering so they are
 * testable without a browser and so two places cannot disagree about what a state means.
 */

/** Active or disabled, as a tone.
 *
 * **Disabled is `unknown`, not `danger`.** A disabled account is an administrative state somebody
 * chose, not a fault — colouring it red would put every offboarded employee in the same visual
 * language as a failing rule. `unknown` also carries the hollow dot, which is the right mark: the
 * account is not in a working state, and that is an absence rather than an alarm. */
/** The tone for a user's account status.
 *
 * **Three tones for three statuses, and the middle one is the reason this is a map rather than a
 * ternary.** `disabled` is `unknown` — the hue this kit reserves for "we cannot act on this" —
 * `active` is `ok`, and `pending_invitation` is `notice`: an invited colleague is neither a
 * problem nor a working account, and rendering them `ok` would show an administrator a list in
 * which somebody who has never signed in looks indistinguishable from somebody who uses the
 * product daily.
 *
 * `satisfies Record<TenantUser['status'], BadgeTone>` — keyed on the *generated* union rather
 * than a hand-written copy of it, so a fourth status added to the contract is a compile error here
 * rather than a badge that renders with the permitted tone — which is the defect the anomaly
 * screen's `verdictTone` shipped with a `default:` branch.
 */
const STATUS_TONE = {
  pending_invitation: 'info',
  active: 'ok',
  disabled: 'unknown',
} satisfies Record<TenantUser['status'], BadgeTone>;

export function statusTone(user: TenantUser): BadgeTone {
  return STATUS_TONE[user.status];
}

/** The i18n key for a user's status. A function rather than a template literal at the call site, so
 * the copy sweep can enumerate the keys this feature needs. */
export function statusLabelKey(user: TenantUser): string {
  return `admin.status.${user.status}`;
}

/** Whether an administrator may resend this person's invitation.
 *
 * **Only for `pending_invitation`, and the refusal matters more than the permission.** The server
 * answers 409 for an account that already has a password, because re-inviting one could only mean
 * clearing it — an administrator-triggered takeover of a colleague's account performed by a button
 * labelled "resend". Offering the button only where it is valid means an administrator never meets
 * that refusal, and a `disabled` account with no password is deliberately excluded too: mailing an
 * activation link for an account that cannot be activated is a message nobody can act on.
 */
export function mayResendInvitation(user: TenantUser): boolean {
  return user.status === 'pending_invitation';
}

/* Roles, ordered so the table reads the same on every tenant.
 *
 * By **privilege**, not alphabetically: an administrator scanning the list is asking "who can do
 * the most", and `admin` sorting under `approver` because of its initial letter makes that question
 * harder for no reason. Custom roles - which a tenant may add - sort after the four starters and
 * then alphabetically among themselves, because nothing here can rank them. */
const STARTER_ORDER: readonly string[] = ['admin', 'approver', 'data-steward', 'reader'];

export function orderRoles(roles: readonly Role[]): readonly Role[] {
  return [...roles].sort((a, b) => {
    const ai = STARTER_ORDER.indexOf(a.name);
    const bi = STARTER_ORDER.indexOf(b.name);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.name.localeCompare(b.name);
  });
}

/* Users, ordered so the ones an administrator acts on come first.
 *
 * Disabled accounts last: they are the ones nothing is pending on. Among the enabled, somebody who
 * has **never signed in** comes first - that is an onboarding somebody is waiting on, and it is the
 * one row in this table with an action attached to it. Everything else falls back to the display
 * name so the order is stable across refetches. */
export function orderUsers(users: readonly TenantUser[]): readonly TenantUser[] {
  return [...users].sort((a, b) => {
    if (a.disabled !== b.disabled) return a.disabled ? 1 : -1;
    const aNever = a.last_login_at === null;
    const bNever = b.last_login_at === null;
    if (aNever !== bNever) return aNever ? -1 : 1;
    return a.display_name.localeCompare(b.display_name);
  });
}

/* Whether a caller may disable this particular user.
 *
 * **Refuses your own account**, matching the server, which answers 422 with the same reason. The
 * server is the enforcement; this exists so the control is not offered and then refused, and so the
 * reason can be shown before the click rather than after it. */
export function canDisable(user: TenantUser, callerSubject: string): boolean {
  return user.subject !== callerSubject;
}

/* Whether a role is one of the four provisioning creates.
 *
 * Surfaced because a starter role can have **drifted**: a tenant provisioned before a module
 * existed does not carry that module's permissions, and nothing refreshes them - `create_roles`
 * refuses to clobber a tenant's edits, so re-provisioning is permission-neutral by design. A screen
 * that could not tell a starter role from a custom one could not explain why two tenants' `reader`
 * roles differ. The flag comes from the API rather than being inferred from the name here, because
 * a tenant may legitimately name a custom role `reader-plus` and this must not guess. */
export function starterLabelKey(role: Role): string {
  return role.is_starter ? 'admin.role.starter' : 'admin.role.custom';
}

/* When somebody last signed in, in the form an administrator reads.
 *
 * **The reference shows `Today 08:14`, `Yesterday 17:02`, `14 Jul` — three shapes, not one.** A
 * column of `8/25/2026, 8:03:25 PM` makes a reader do arithmetic to answer the only question they
 * have: is this account in use. Today wants a clock time, yesterday wants the word, and anything
 * older wants a date — the precision that matters falls off with age.
 *
 * `now` is a parameter rather than read from the clock, for the reason `activityWhen` in the home
 * feature already is: a function that reads the clock cannot be tested, and a component that calls
 * one re-renders with a different answer on every unrelated state change.
 *
 * **Calendar days, not 24-hour blocks.** A sign-in at 23:00 yesterday is "yesterday" at 08:00 today,
 * and "nine hours ago" is a sentence nobody says. Differenced on the local date parts so it respects
 * the reader's own midnight rather than UTC's.
 */
export type LastSeen =
  /** Never signed in. A different fact from a dormant account, and the one an onboarding chase acts
   * on — so it is never a dash and never an epoch. */
  | { readonly kind: 'never' }
  | { readonly kind: 'today'; readonly at: Date }
  | { readonly kind: 'yesterday'; readonly at: Date }
  | { readonly kind: 'earlier'; readonly at: Date };

const HOURS_IN_A_DAY = 24;

export function lastSeen(lastLoginAt: string | null, now: Date): LastSeen {
  if (lastLoginAt === null) return { kind: 'never' };
  const at = new Date(lastLoginAt);
  /* An unparseable timestamp reads as never rather than rendering `Invalid Date`. The API types this
   * as a date-time, so this guards a shape the contract forbids — which is what a guard is for. */
  if (Number.isNaN(at.getTime())) return { kind: 'never' };

  const midnight = (moment: Date): number =>
    new Date(moment.getFullYear(), moment.getMonth(), moment.getDate()).getTime();
  const days = Math.round(
    (midnight(now) - midnight(at)) / (HOURS_IN_A_DAY * 3600 * 1000),
  );

  /* A future timestamp reads as today rather than as a negative day count. Clock skew between this
   * platform and a browser is real and unbounded, and "in -1 days" is a rendering bug that looks
   * like data. */
  if (days <= 0) return { kind: 'today', at };
  if (days === 1) return { kind: 'yesterday', at };
  return { kind: 'earlier', at };
}
